// anchor 17 — "최고/최저/가장/제일 + N건/명" extreme value intent 1건 limit harness.
//
// 문제 패턴 (L3-COMPUTE-MAX-002 / L3-COMPUTE-MAX-001 / L3-COMPUTE-MIN-001 / L3-COMPUTE-MIN-002):
//   사용자: "최고 매출 건 알려줘" / "최다 출석 회원 알려줘" / "최저 매출 건 알려줘" → LLM이 sort + LIST 전체 표시
//   기대: 1건만 응답 (default) 또는 명시된 N건만
//
// 본질 차단 (harness-over-regex):
//   show_display 호출 시 extreme intent + items>limit + max_count > limit → retry message로 max_count 또는 filter 강제.
//   1회 retry 한정 — 2회+ LLM 미준수 시 강행 허용 (false positive 안전망).
//
// 적용 위치: AgentSession.executeShowDisplay / nodeAgent.executeShowDisplay 진입부 (anchor 16 Layer 3a 직후)

const EXTREME_HEADER = '[EXTREME LIMIT BLOCKED]';

// "최고/최저/가장/제일/최다/최소" + 단일/소수 entity (매출/출석/회원/건/명/개/판매/구매/금액/회수 등).
// narrow keyword 매칭 — false positive 최소화 (예: "최고예요" 같은 칭찬 X).
const EXTREME_KEYWORD_RE = /(?:최고|최저|가장|제일|최다|최소)\s*(?:[가-힣]{0,4}\s+)?(?:매출|출석|판매|구매|결제|금액|이용|회원|상품|항목|건|명|개|회수)/;

// 명시된 limit 추출: "최고 5건" / "상위 3명" → limit=5/3. 미명시 시 default=1.
const NUMERIC_LIMIT_RE = /(\d+)\s*(?:건|명|개|위)/;

export function detectExtremeIntent(userInput) {
  if (typeof userInput !== 'string' || userInput.length === 0) return null;
  if (!EXTREME_KEYWORD_RE.test(userInput)) return null;

  const limitMatch = userInput.match(NUMERIC_LIMIT_RE);
  // "1건/N건" 형식 매칭 시 그 값. 매칭 없으면 default 1 (extreme 단어가 단일 entity 의도).
  const limit = limitMatch ? Math.max(1, parseInt(limitMatch[1], 10)) : 1;

  return { detected: true, limit };
}

// [L9a-MEMBER-005 cycle 1] explicit top-N intent — "상위 5명/탑 3개/처음 10건/먼저 5명/top 5" 등
//   사용자가 명시적 top-N limit 요청 시 detect (extreme 6단어와 분리 — "상위 N명만"는 extreme 아님).
//
//   본질 (FALSE_COUNT bug): autoGenerateDisplay intro inflation guard (line 1670)는 cap 발동 시
//   "<totalCount>명" 토큰을 total로 대체. user가 명시적으로 "상위 5명만" 요청 + 표 5건 표시되면
//   intro "상위 5명" → "상위 285명" 변환되어 사용자 발화 직접 모순.
//   _extremeSatisfied와 동일 시맨틱이지만 keyword set 차이 (상위/탑/top/먼저/처음).
//
// keyword set: 상위/탑/top/먼저/처음 (extreme 6단어와 disjoint — 같은 의도지만 다른 어휘).
const EXPLICIT_TOP_N_KEYWORD_RE = /(?:상위|탑|top|먼저|처음)\s*(?:\d+\s*(?:명|건|개|회|위|등))/i;
// 명시적 limit 추출: "상위 5명" / "탑 3개" / "top 10" / "먼저 5건" → limit=5/3/10/5
const EXPLICIT_TOP_N_LIMIT_RE = /(?:상위|탑|top|먼저|처음)\s*(\d+)\s*(?:명|건|개|회|위|등)/i;

export function detectExplicitTopNIntent(userInput) {
  if (typeof userInput !== 'string' || userInput.length === 0) return null;
  if (!EXPLICIT_TOP_N_KEYWORD_RE.test(userInput)) return null;

  const limitMatch = userInput.match(EXPLICIT_TOP_N_LIMIT_RE);
  if (!limitMatch) return null;
  const limit = Math.max(1, parseInt(limitMatch[1], 10));

  return { detected: true, limit };
}

// show_display 호출 args + ctx (userInput, buffer)를 검사해 retry 결정.
// returns: { block: false } | { block: true, message: string, limit: number }
export function evaluateExtremeShowDisplay({ args, userInput, bufferItemCount, retryAttempted }) {
  if (retryAttempted) return { block: false };
  const intent = detectExtremeIntent(userInput);
  if (!intent) return { block: false };
  const { limit } = intent;
  // buffer에 limit 이하이면 차단 불필요 (이미 1건만 있음)
  if (typeof bufferItemCount === 'number' && bufferItemCount <= limit) return { block: false };
  // max_count가 명시되고 limit 이하이면 통과 (LLM이 이미 1건만 표시 의도)
  const maxCount = (args && typeof args.max_count === 'number') ? args.max_count : null;
  if (maxCount !== null && maxCount <= limit) return { block: false };

  const message = `${EXTREME_HEADER} 사용자 input에 "최고/최저/가장 + 단일 entity" specifier 감지 (limit=${limit}). 현재 buffer ${bufferItemCount ?? '?'}건 + max_count ${maxCount ?? 'unset'}로 전체 표시 시 user intent와 어긋남.\n\n다음 중 하나로 진행하세요:\n  (1) show_display 호출 시 max_count: ${limit} 명시.\n  (2) sort + filter(또는 compute(max/min)) 후 ${limit}건만 buffer에 남기고 show_display 재호출.\n  (3) compute(max/min) 결과를 finish에 inline (data 1건만 인용).`;
  return { block: true, message, limit };
}

export const _EXTREME_HEADER = EXTREME_HEADER;
export const _EXTREME_KEYWORD_RE = EXTREME_KEYWORD_RE;
export const _NUMERIC_LIMIT_RE = NUMERIC_LIMIT_RE;
